import { ApolloClient } from '@apollo/client';
import { ConfigurationStatus, ControlAction } from '../../domain/enums';
import {
  ActionsCatalog,
  AssessmentsList,
  BatchesPage,
  Configuration,
  ControlResult,
  CreateConfigurationInput,
  FileRecordPreview,
  IngestionDetail,
  IngestionSummary,
  IngestionSummaryStats,
  ListBatchesParams,
  ListConfigurationsParams,
  ListIngestionsParams,
  ListRecordsParams,
  PaginatedResult,
  ProviderSummary,
  UpdateConfigurationInput,
  ValidationReport,
} from '../../domain/types';
import { mapWithConcurrency } from '../../utils/concurrency.util';
import {
  buildUnavailableProviderSummary,
  PROVIDER_SAMPLE_SIZE,
  summarizeProviderIngestions,
} from '../../utils/provider-summary.utils';
import { PartnerDataClient } from '../client';
import { isNotFoundError } from './errors';
import {
  pdgConfigurationStatus,
  pdgControlAction,
  toActionsCatalog,
  toAssessmentsList,
  toBatchesPage,
  toConfiguration,
  toControlResult,
  toFileRecordPreview,
  toIngestionDetail,
  toIngestionSummary,
  toIngestionSummaryStats,
  toListBatchesInput,
  toListConfigurationsInput,
  toListIngestionsInput,
  toPaginatedResult,
  toValidationReport,
} from './mappers';
import {
  ControlIngestionDocument,
  ControlIngestionMutation,
  ControlIngestionMutationVariables,
  CreateConfigurationDocument,
  CreateConfigurationMutation,
  CreateConfigurationMutationVariables,
  GetActionsCatalogDocument,
  GetActionsCatalogQuery,
  GetAssessmentsDocument,
  GetAssessmentsQuery,
  GetAssessmentsQueryVariables,
  GetConfigurationDocument,
  GetConfigurationQuery,
  GetConfigurationQueryVariables,
  GetDataProvidersDocument,
  GetDataProvidersQuery,
  GetIngestionDocument,
  GetIngestionQuery,
  GetIngestionQueryVariables,
  GetIngestionSummaryDocument,
  GetIngestionSummaryQuery,
  GetIngestionSummaryQueryVariables,
  GetValidationDocument,
  GetValidationQuery,
  GetValidationQueryVariables,
  ListBatchesDocument,
  ListBatchesQuery,
  ListBatchesQueryVariables,
  ListConfigurationsDocument,
  ListConfigurationsQuery,
  ListConfigurationsQueryVariables,
  ListIngestionsDocument,
  ListIngestionsQuery,
  ListIngestionsQueryVariables,
  ListRecordsDocument,
  ListRecordsQuery,
  ListRecordsQueryVariables,
  UpdateConfigurationDocument,
  UpdateConfigurationMutation,
  UpdateConfigurationMutationVariables,
  UpdateConfigurationStatusDocument,
  UpdateConfigurationStatusMutation,
  UpdateConfigurationStatusMutationVariables,
} from './types';

/**
 * Adapter #1 — implements the `PartnerDataClient` port by calling the
 * `Pdg*` GraphQL surface exposed by operations-hub-bff through a plain
 * `ApolloClient` instance.
 *
 * Documents, operation result types and operation variable types all come
 * from the generated `./types`; every response goes through `./mappers` on
 * the way out, so nothing GraphQL-shaped crosses this file's boundary. The
 * generated Apollo hooks in `./types` are deliberately unused — the port is
 * plain Promise-returning functions, which is what makes it swappable.
 *
 * This takes an `ApolloClient` rather than relying on React context so the
 * port methods can be plain, testable, Promise-returning functions — no
 * React hook rules apply here. `use-partner-data-apollo-client.ts` is the
 * only place that wires this adapter up to the app's Apollo context, and it
 * is only ever imported through `api/use-partner-data-client.ts`.
 *
 * Reads use `network-only`: operators are watching a live pipeline, so a
 * cached page is actively misleading.
 */

/** How long one fan-out result is reused before probing PDGS again. */
export const PROVIDER_SUMMARY_CACHE_TTL_MS = 60_000;
/** Concurrent per-provider probes. ~100 providers → ~17 rounds. */
export const PROVIDER_SUMMARY_CONCURRENCY = 6;
/** Newest ingestions scanned to find providers that have no configuration. */
export const RECENT_INGESTIONS_SCAN_LIMIT = 100;

interface ProviderSummaryCache {
  expiresAt: number;
  value: ProviderSummary[];
}

export const createPartnerDataApolloClient = (
  apolloClient: ApolloClient<object>,
): PartnerDataClient => {
  let providerSummaryCache: ProviderSummaryCache | null = null;
  let providerSummaryInFlight: Promise<ProviderSummary[]> | null = null;

  const fetchIngestionsPage = async (
    params: ListIngestionsParams,
  ): Promise<PaginatedResult<IngestionSummary>> => {
    const { data } = await apolloClient.query<
      ListIngestionsQuery,
      ListIngestionsQueryVariables
    >({
      query: ListIngestionsDocument,
      variables: { input: toListIngestionsInput(params) },
      fetchPolicy: 'network-only',
    });

    return toPaginatedResult(data.pdgIngestions, toIngestionSummary);
  };

  const fetchDataProviders = async (): Promise<string[]> => {
    const { data } = await apolloClient.query<GetDataProvidersQuery>({
      query: GetDataProvidersDocument,
      fetchPolicy: 'network-only',
    });

    return data.pdgDataProviders.items;
  };

  /**
   * Only the fetch is guarded: one provider PDGS cannot serve degrades to an
   * "unavailable" card, but a bug in the summarizer is ours and has to
   * reject the whole fan-out rather than hide behind `loadError`.
   */
  const probeProvider = async (
    dataProvider: string,
    hasConfiguration: boolean,
  ): Promise<ProviderSummary> => {
    let page: PaginatedResult<IngestionSummary>;

    try {
      page = await fetchIngestionsPage({
        dataProvider,
        limit: PROVIDER_SAMPLE_SIZE,
      });
    } catch {
      return buildUnavailableProviderSummary(dataProvider, hasConfiguration);
    }

    return summarizeProviderIngestions({
      dataProvider,
      hasConfiguration,
      ingestions: page.items,
    });
  };

  /**
   * `pdgDataProviders` is read from the configurations table, so a bucket
   * that is sending files with no configuration would never appear. Scan
   * one page of the newest ingestions and add any provider not configured.
   *
   * The unconfigured tail is sorted with `localeCompare` rather than a bare
   * `sort()`: these names are partner bucket names, so they can carry mixed
   * case or diacritics, and the default code-unit order would put `Globex`
   * before `acme` and `ärzte-gmbh` after `beta` in the operator-facing table.
   */
  const buildProviderSummaries = async (): Promise<ProviderSummary[]> => {
    const [configured, recent] = await Promise.all([
      fetchDataProviders(),
      fetchIngestionsPage({ limit: RECENT_INGESTIONS_SCAN_LIMIT }),
    ]);
    const configuredSet = new Set(configured);
    const unconfigured = Array.from(
      new Set(
        recent.items
          .map(ingestion => ingestion.dataProvider)
          .filter(
            (name): name is string => name !== null && !configuredSet.has(name),
          ),
      ),
    ).sort((a, b) => a.localeCompare(b));
    const targets = [
      ...configured.map(name => ({ name, hasConfiguration: true })),
      ...unconfigured.map(name => ({ name, hasConfiguration: false })),
    ];

    return mapWithConcurrency(targets, PROVIDER_SUMMARY_CONCURRENCY, target =>
      probeProvider(target.name, target.hasConfiguration),
    );
  };

  return {
    async listIngestions(
      params: ListIngestionsParams = {},
    ): Promise<PaginatedResult<IngestionSummary>> {
      return fetchIngestionsPage(params);
    },

    async getIngestion(eventId: string): Promise<IngestionDetail> {
      const { data } = await apolloClient.query<
        GetIngestionQuery,
        GetIngestionQueryVariables
      >({
        query: GetIngestionDocument,
        variables: { eventId },
        fetchPolicy: 'network-only',
      });

      return toIngestionDetail(data.pdgIngestion);
    },

    async getSummary(windowHours?: number): Promise<IngestionSummaryStats> {
      const { data } = await apolloClient.query<
        GetIngestionSummaryQuery,
        GetIngestionSummaryQueryVariables
      >({
        query: GetIngestionSummaryDocument,
        variables: { windowHours },
        fetchPolicy: 'network-only',
      });

      return toIngestionSummaryStats(data.pdgIngestionSummary);
    },

    async controlIngestion(
      eventId: string,
      action: ControlAction,
    ): Promise<ControlResult> {
      const { data } = await apolloClient.mutate<
        ControlIngestionMutation,
        ControlIngestionMutationVariables
      >({
        mutation: ControlIngestionDocument,
        variables: { eventId, action: pdgControlAction[action] },
      });

      if (!data) {
        throw new Error('controlIngestion: mutation returned no data');
      }

      return toControlResult(data.pdgControlIngestion);
    },

    async listBatches(
      eventId: string,
      params: ListBatchesParams = {},
    ): Promise<BatchesPage> {
      const { data } = await apolloClient.query<
        ListBatchesQuery,
        ListBatchesQueryVariables
      >({
        query: ListBatchesDocument,
        variables: { eventId, input: toListBatchesInput(params) },
        fetchPolicy: 'network-only',
      });

      return toBatchesPage(data.pdgIngestionBatches);
    },

    async getValidation(eventId: string): Promise<ValidationReport | null> {
      try {
        const { data } = await apolloClient.query<
          GetValidationQuery,
          GetValidationQueryVariables
        >({
          query: GetValidationDocument,
          variables: { eventId },
          fetchPolicy: 'network-only',
        });

        return toValidationReport(data.pdgIngestionValidation);
      } catch (error) {
        // PDGS 404s until the validator has written its (write-once) report.
        // That is "has not run yet", not a failure, so it maps to an empty
        // result instead of propagating and turning the tab into an error
        // panel. Every other failure still throws.
        if (isNotFoundError(error)) {
          return null;
        }

        throw error;
      }
    },

    async getAssessments(eventId: string): Promise<AssessmentsList> {
      const { data } = await apolloClient.query<
        GetAssessmentsQuery,
        GetAssessmentsQueryVariables
      >({
        query: GetAssessmentsDocument,
        variables: { eventId },
        fetchPolicy: 'network-only',
      });

      return toAssessmentsList(data.pdgIngestionAssessments);
    },

    async listRecords(
      eventId: string,
      params: ListRecordsParams,
    ): Promise<PaginatedResult<FileRecordPreview>> {
      const { data } = await apolloClient.query<
        ListRecordsQuery,
        ListRecordsQueryVariables
      >({
        query: ListRecordsDocument,
        variables: { eventId, input: params },
        fetchPolicy: 'network-only',
      });

      return toPaginatedResult(data.pdgIngestionRecords, toFileRecordPreview);
    },

    async getDataProviders(): Promise<string[]> {
      return fetchDataProviders();
    },

    async listProviderSummaries({
      bypassCache = false,
    }: { bypassCache?: boolean } = {}): Promise<ProviderSummary[]> {
      if (
        !bypassCache &&
        providerSummaryCache &&
        Date.now() < providerSummaryCache.expiresAt
      ) {
        return providerSummaryCache.value;
      }

      // The overview mounts several consumers at once. Without this, each
      // would start its own ~100-request fan-out before the first finished
      // and had anything to cache.
      if (!bypassCache && providerSummaryInFlight) {
        return providerSummaryInFlight;
      }

      // The TTL starts when the fan-out lands, not when it was requested: a
      // slow fan-out would otherwise expire the moment it arrived.
      const pending = buildProviderSummaries().then(value => {
        providerSummaryCache = {
          expiresAt: Date.now() + PROVIDER_SUMMARY_CACHE_TTL_MS,
          value,
        };

        return value;
      });

      providerSummaryInFlight = pending;

      try {
        return await pending;
      } finally {
        // A rejected fan-out must not be handed to the next caller.
        if (providerSummaryInFlight === pending) {
          providerSummaryInFlight = null;
        }
      }
    },

    async listConfigurations(
      params: ListConfigurationsParams = {},
    ): Promise<PaginatedResult<Configuration>> {
      const { data } = await apolloClient.query<
        ListConfigurationsQuery,
        ListConfigurationsQueryVariables
      >({
        query: ListConfigurationsDocument,
        variables: { input: toListConfigurationsInput(params) },
        fetchPolicy: 'network-only',
      });

      return toPaginatedResult(data.pdgConfigurations, toConfiguration);
    },

    async getConfiguration(id: string): Promise<Configuration> {
      const { data } = await apolloClient.query<
        GetConfigurationQuery,
        GetConfigurationQueryVariables
      >({
        query: GetConfigurationDocument,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      return toConfiguration(data.pdgConfiguration);
    },

    async createConfiguration(
      input: CreateConfigurationInput,
    ): Promise<Configuration> {
      const { data } = await apolloClient.mutate<
        CreateConfigurationMutation,
        CreateConfigurationMutationVariables
      >({
        mutation: CreateConfigurationDocument,
        variables: { input },
      });

      if (!data) {
        throw new Error('createConfiguration: mutation returned no data');
      }

      return toConfiguration(data.pdgCreateConfiguration);
    },

    async updateConfiguration(
      id: string,
      input: UpdateConfigurationInput,
    ): Promise<Configuration> {
      const { data } = await apolloClient.mutate<
        UpdateConfigurationMutation,
        UpdateConfigurationMutationVariables
      >({
        mutation: UpdateConfigurationDocument,
        variables: { id, input },
      });

      if (!data) {
        throw new Error('updateConfiguration: mutation returned no data');
      }

      return toConfiguration(data.pdgUpdateConfiguration);
    },

    async updateConfigurationStatus(
      id: string,
      status: ConfigurationStatus,
    ): Promise<Configuration> {
      const { data } = await apolloClient.mutate<
        UpdateConfigurationStatusMutation,
        UpdateConfigurationStatusMutationVariables
      >({
        mutation: UpdateConfigurationStatusDocument,
        variables: { id, status: pdgConfigurationStatus[status] },
      });

      if (!data) {
        throw new Error('updateConfigurationStatus: mutation returned no data');
      }

      return toConfiguration(data.pdgUpdateConfigurationStatus);
    },

    async getActionsCatalog(): Promise<ActionsCatalog> {
      const { data } = await apolloClient.query<GetActionsCatalogQuery>({
        query: GetActionsCatalogDocument,
        fetchPolicy: 'network-only',
      });

      return toActionsCatalog(data.pdgActionsCatalog);
    },
  };
};
